import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, FolderPlus, FolderTree, Loader2, Plus } from "lucide-react";
import { TemplateTreeContext } from "./TemplateTreeContext";
import { TemplateNode } from "./TemplateNode";
import { NewTemplateInput } from "./NewTemplateInput";
import type { DocumentFolderTemplate } from "@/hooks/use-document-folder-templates";

interface DocumentTemplatesSectionProps {
  templates: DocumentFolderTemplate[];
  isLoading: boolean;
  error?: Error | null;
  isAdmin: boolean;
  isCreating: boolean;
  rootTemplates: DocumentFolderTemplate[];
  ctxValue: React.ContextType<typeof TemplateTreeContext>;
  onCreateRoot: () => void;
  onRetry?: () => void;
  handleCreate: (parentId: number | null) => Promise<void>;
}

export function DocumentTemplatesSection({
  templates,
  isLoading,
  error,
  isAdmin,
  isCreating,
  rootTemplates,
  ctxValue,
  onCreateRoot,
  onRetry,
  handleCreate,
}: DocumentTemplatesSectionProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FolderTree className="h-5 w-5" />
              Document Folder Templates
              {templates.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {templates.filter((t) => t.isActive).length}
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="mt-1.5">
              Define folder structures that are automatically created for every new claim.
            </CardDescription>
          </div>
          {isAdmin && (
            <Button
              size="sm"
              onClick={onCreateRoot}
              disabled={isCreating}
              className="w-full sm:w-auto"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Folder
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error Loading Templates</AlertTitle>
            <AlertDescription className="mt-2 flex items-center justify-between gap-3">
              <span>{error instanceof Error ? error.message : "Failed to load document folder templates"}</span>
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Retry
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !error && (
          <>
            <TemplateTreeContext.Provider value={ctxValue}>
              {ctxValue?.creatingInParent === "root" && (
                <NewTemplateInput
                  value={ctxValue.newName}
                  onChange={ctxValue.setNewName}
                  onSubmit={() => handleCreate(null)}
                  onCancel={ctxValue.onCreateCancel}
                  depth={0}
                  isCreating={isCreating}
                />
              )}

              {rootTemplates.length === 0 && ctxValue?.creatingInParent !== "root" ? (
                <div className="text-center py-12">
                  <FolderTree className="h-12 w-12 mx-auto text-muted-foreground/40 mb-4" />
                  <h3 className="text-sm font-medium text-muted-foreground mb-1">No Folder Templates</h3>
                  <p className="text-xs text-muted-foreground mb-4">
                    New claims won't have any pre-created folders. Add templates to auto-generate a folder structure.
                  </p>
                  {isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={onCreateRoot}
                    >
                      <FolderPlus className="h-4 w-4 mr-1.5" />
                      Create First Template
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-0.5">
                  {rootTemplates.map((template) => (
                    <TemplateNode key={template.id} template={template} depth={0} />
                  ))}
                </div>
              )}
            </TemplateTreeContext.Provider>

            {templates.length > 0 && (
              <p className="text-xs text-muted-foreground mt-4 pt-4 border-t">
                Changes here do <strong>not</strong> affect folders on existing claims — only new claims going forward.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
